import React, { memo, useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTableContext } from '../../context/TableContext';
import { getFieldType } from '../../../config/customFieldTypes';
import './CustomFieldCell.css';

/**
 * CustomFieldCell Component
 * 
 * Renders a custom field value in the table view.
 * Supports inline editing for all field types.
 */
const CustomFieldCell = memo(({ row, fieldId }) => {
  const { customFields, onCustomFieldValueChange } = useTableContext();
  const task = row.original;
  
  // Find the field definition
  const field = customFields?.find(f => f.id === fieldId);
  const fieldType = field ? getFieldType(field.type) : null;
  const value = task?.customFieldValues?.[fieldId];
  
  // Editing state
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value ?? '');
  const inputRef = useRef(null);

  // Sync local value when task value changes externally
  useEffect(() => {
    if (!isEditing) {
      setLocalValue(value ?? '');
    }
  }, [value, isEditing]);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current.select) {
        inputRef.current.select();
      }
    }
  }, [isEditing]);

  // Handle save
  const handleSave = useCallback(() => {
    if (onCustomFieldValueChange && task?.id) {
      onCustomFieldValueChange(task.id, fieldId, localValue);
    }
    setIsEditing(false);
  }, [task?.id, fieldId, localValue, onCustomFieldValueChange]);

  // Handle key down
  const handleKeyDown = useCallback((e) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setLocalValue(value ?? '');
      setIsEditing(false);
    }
  }, [handleSave, value]);

  // Handle blur
  const handleBlur = useCallback(() => {
    handleSave();
  }, [handleSave]);

  // Handle checkbox toggle (doesn't use edit mode)
  const handleCheckboxClick = useCallback((e) => {
    e.stopPropagation();
    if (onCustomFieldValueChange && task?.id) {
      onCustomFieldValueChange(task.id, fieldId, !value);
    }
  }, [task?.id, fieldId, value, onCustomFieldValueChange]);

  // Handle cell click to start editing
  const handleClick = useCallback(() => {
    if (field?.type !== 'checkbox') {
      setIsEditing(true);
    }
  }, [field?.type]);

  if (!field || !fieldType) {
    return <div className="custom-field-cell custom-field-cell--empty">—</div>;
  }

  // Checkbox type - always show checkbox, no edit mode
  if (field.type === 'checkbox') {
    const isChecked = value === true;
    return (
      <div className="custom-field-cell custom-field-cell--checkbox" onClick={handleCheckboxClick}>
        <span className={`custom-field-cell__checkbox ${isChecked ? 'custom-field-cell__checkbox--checked' : ''}`}>
          {isChecked && (
            <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="2,6 5,9 10,3" />
            </svg>
          )}
        </span>
      </div>
    );
  }

  // Select type - custom dropdown (matches PriorityDropdown style)
  if (field.type === 'select') {
    return (
      <SelectDropdownCell
        field={field}
        value={value}
        taskId={task?.id}
        onCustomFieldValueChange={onCustomFieldValueChange}
      />
    );
  }

  // Date type
  if (field.type === 'date') {
    if (isEditing) {
      return (
        <div className="custom-field-cell custom-field-cell--editing">
          <input
            ref={inputRef}
            type="date"
            className="custom-field-cell__input custom-field-cell__input--date"
            value={localValue || ''}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
          />
        </div>
      );
    }

    const displayValue = value ? new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : null;
    return (
      <div className="custom-field-cell" onClick={handleClick}>
        {displayValue ? (
          <span className="custom-field-cell__value">{displayValue}</span>
        ) : (
          <span className="custom-field-cell__empty">—</span>
        )}
      </div>
    );
  }

  // Number type
  if (field.type === 'number') {
    if (isEditing) {
      return (
        <div className="custom-field-cell custom-field-cell--editing">
          <input
            ref={inputRef}
            type="number"
            className="custom-field-cell__input custom-field-cell__input--number"
            value={localValue ?? ''}
            onChange={(e) => setLocalValue(e.target.value === '' ? null : Number(e.target.value))}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            placeholder="0"
          />
        </div>
      );
    }

    return (
      <div className="custom-field-cell" onClick={handleClick}>
        {value !== undefined && value !== null ? (
          <span className="custom-field-cell__value custom-field-cell__value--number">
            {typeof value === 'number' ? value.toLocaleString() : value}
          </span>
        ) : (
          <span className="custom-field-cell__empty">—</span>
        )}
      </div>
    );
  }

  // URL type
  if (field.type === 'url') {
    if (isEditing) {
      return (
        <div className="custom-field-cell custom-field-cell--editing">
          <input
            ref={inputRef}
            type="url"
            className="custom-field-cell__input"
            value={localValue || ''}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            placeholder="https://..."
          />
        </div>
      );
    }

    if (value) {
      let displayUrl = value;
      try {
        const url = new URL(value);
        displayUrl = url.hostname.replace('www.', '');
      } catch {}
      
      return (
        <div className="custom-field-cell">
          <a 
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            className="custom-field-cell__link"
            onClick={(e) => e.stopPropagation()}
          >
            {displayUrl}
          </a>
        </div>
      );
    }

    return (
      <div className="custom-field-cell" onClick={handleClick}>
        <span className="custom-field-cell__empty">—</span>
      </div>
    );
  }

  // Text type (default)
  if (isEditing) {
    return (
      <div className="custom-field-cell custom-field-cell--editing">
        <input
          ref={inputRef}
          type="text"
          className="custom-field-cell__input"
          value={localValue || ''}
          onChange={(e) => setLocalValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={fieldType?.placeholder || 'Enter text...'}
        />
      </div>
    );
  }

  return (
    <div className="custom-field-cell" onClick={handleClick}>
      {value ? (
        <span className="custom-field-cell__value" title={value}>{value}</span>
      ) : (
        <span className="custom-field-cell__empty">—</span>
      )}
    </div>
  );
});

CustomFieldCell.displayName = 'CustomFieldCell';

/**
 * SelectDropdownCell - Custom dropdown for select fields
 * Uses portal for proper rendering outside table cells
 */
const SelectDropdownCell = memo(({ field, value, taskId, onCustomFieldValueChange }) => {
  const [showMenu, setShowMenu] = useState(false);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

  // Unique ID for this dropdown instance
  const dropdownId = useRef(`customfield-${taskId}-${field?.id}`).current;

  const selectedOption = field.options?.find(opt => opt.id === value);
  const displayLabel = selectedOption?.label || 'None';
  const displayColor = selectedOption?.color || '#6b7280';

  // Calculate menu position when opening
  useEffect(() => {
    if (showMenu && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 4,
        left: rect.left,
      });
    }
  }, [showMenu]);

  // Close menu when clicking outside
  useEffect(() => {
    if (!showMenu) return;

    const handleClickOutside = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setShowMenu(false);
    };

    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, [showMenu]);

  // Close on scroll
  useEffect(() => {
    if (!showMenu) return;
    
    const handleScroll = () => setShowMenu(false);
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [showMenu]);

  // Close when another dropdown opens (only listen when this menu is open)
  useEffect(() => {
    if (!showMenu) return;
    
    const handleCloseDropdowns = (e) => {
      if (e.detail?.exceptId !== dropdownId) {
        setShowMenu(false);
      }
    };
    
    window.addEventListener('closeTableDropdowns', handleCloseDropdowns);
    return () => window.removeEventListener('closeTableDropdowns', handleCloseDropdowns);
  }, [showMenu, dropdownId]);

  const handleClick = useCallback((e) => {
    e.stopPropagation();
    if (!showMenu) {
      // Close other dropdowns before opening this one
      window.dispatchEvent(new CustomEvent('closeTableDropdowns', { detail: { exceptId: dropdownId } }));
    }
    setShowMenu(!showMenu);
  }, [showMenu, dropdownId]);

  const handleSelect = useCallback((optionId) => {
    onCustomFieldValueChange?.(taskId, field.id, optionId);
    setShowMenu(false);
  }, [onCustomFieldValueChange, taskId, field.id]);

  const menuContent = showMenu && (
    <div 
      ref={menuRef}
      className="custom-field-cell__dropdown-menu"
      style={{ 
        position: 'fixed',
        top: menuPosition.top, 
        left: menuPosition.left,
        zIndex: 10000,
      }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* None option */}
      <button
        className={`custom-field-cell__dropdown-option ${!value ? 'custom-field-cell__dropdown-option--active' : ''}`}
        onClick={() => handleSelect(null)}
      >
        <span className="custom-field-cell__dropdown-dot" style={{ background: '#6b7280' }} />
        <span className="custom-field-cell__dropdown-label">None</span>
      </button>
      {/* Field options */}
      {field.options?.map(option => (
        <button
          key={option.id}
          className={`custom-field-cell__dropdown-option ${option.id === value ? 'custom-field-cell__dropdown-option--active' : ''}`}
          onClick={() => handleSelect(option.id)}
        >
          <span className="custom-field-cell__dropdown-dot" style={{ background: option.color }} />
          <span className="custom-field-cell__dropdown-label">{option.label}</span>
        </button>
      ))}
    </div>
  );

  return (
    <>
      <button 
        ref={btnRef}
        className="custom-field-cell__dropdown-btn"
        onClick={handleClick}
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          '--field-color': displayColor,
          '--field-bg': `${displayColor}15`,
        }}
      >
        <span className="custom-field-cell__dropdown-dot" style={{ background: displayColor }} />
        <span className="custom-field-cell__dropdown-value">
          {displayLabel}
        </span>
        <span className="custom-field-cell__dropdown-arrow">▾</span>
      </button>
      {showMenu && createPortal(menuContent, document.body)}
    </>
  );
});

SelectDropdownCell.displayName = 'SelectDropdownCell';

export default CustomFieldCell;
